import React from "react";
import {
  Button,
  Iconography,
  IconographySizes,
  IconographyThemes,
  Loader,
  LoaderSizes,
  Modal,
  ModalVariants,
} from "@hid-galaxy-ui/galaxy-react";
import { useAppDispatch, useAppSelector } from "../../../../../../../../redux";
import { deleteApplicationIDSagaAction } from "../../../../../../../../redux/saga/manage-application/saga-actions";
import "./DeleteApplicationModal.scss";

interface ApplicationData {
  project: string;
  technology: number[];
  select_id: "sandbox_application_id" | "production_application_id";
  application_id: string;
  cert: boolean;
  preprod: boolean;
  prod: boolean;
}

const DeleteApplicationModal: React.FC<{
  applicationData: ApplicationData;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  clearApplicationData: () => void;
}> = ({ applicationData, setModalOpen, clearApplicationData }) => {
  const [submitted, setSubmitted] = React.useState<boolean>(false);
  const { status } = useAppSelector((state) => state.manage_application);
  const { partnerDetails } = useAppSelector((state) => state.partner_details);
  const dispatch = useAppDispatch();

  const onConfirmButtonClick = () => {
    setSubmitted(true);
    const environment = [
      applicationData.cert ? "cert" : "",
      applicationData.preprod ? "preprod" : "",
      applicationData.prod ? "prod" : "",
    ].filter(Boolean);
    if (environment.length > 0) {
      dispatch(
        deleteApplicationIDSagaAction({
          application_id: applicationData.application_id.trim(),
          select_id: applicationData.select_id,
          environment: environment,
          project_name: applicationData.project,
          partner_id: partnerDetails?.ID,
        })
      );
    }
  };

  const footerConfig = submitted
    ? {}
    : {
        secondaryBtnLabel: "Cancel",
      };

  const onModalClose = () => {
    setModalOpen(false);
    if (submitted && status.deleteApplicationStatus) {
      clearApplicationData();
    }
  };

  return (
    <Modal
      headerText="Delete Application ID"
      size={ModalVariants.Medium}
      footerConfig={footerConfig}
      onClose={() => onModalClose()}
      className="delete-application-modal"
    >
      {submitted ? (
        <div className="status-message-modal-container">
          {status.deleteApplicationLoading ? (
            <Loader variant={LoaderSizes.large} />
          ) : status.deleteApplicationStatus ? (
            <>
              <Iconography
                icon="circleCheck"
                size={IconographySizes.XLarge}
                theme={IconographyThemes.Success}
              />
              <div className="message">Application ID Deleted Successfully</div>
              <Button
                variant="primary"
                label="Continue"
                onClick={() => onModalClose()}
              />
            </>
          ) : (
            <>
              <Iconography
                icon="circleXmark"
                size={IconographySizes.XLarge}
                theme={IconographyThemes.Error}
              />
              <div className="message">{status.deleteApplicationMessage}</div>
              <Button
                variant="primary"
                label="Continue"
                onClick={() => setModalOpen(false)}
              />
            </>
          )}
        </div>
      ) : (
        <div className="message-button-container">
          <p className="confirm-message">
            Please confirm that you want to delete this application ID
            permanently.
            <br />
            <b>
              This will remove the application ID from all technologies
              associated with it.
            </b>
          </p>
          <div className="icon-wrapper">
            <Iconography icon="key" size={IconographySizes.Medium} />
          </div>
          <div className="application-id">{applicationData.application_id}</div>
          <div className="environment-status">
            <div className="status-item">
              <span className="label">CERT:</span>
              <span className="value">
                {applicationData.cert ? "Yes" : "No"}
              </span>
            </div>
            <div className="status-item">
              <span className="label">Preprod:</span>
              <span className="value">
                {applicationData.preprod ? "Yes" : "No"}
              </span>
            </div>
            <div className="status-item">
              <span className="label">Prod:</span>
              <span className="value">
                {applicationData.prod ? "Yes" : "No"}
              </span>
            </div>
          </div>
          <Button
            variant="warning"
            label="Confirm and Delete Application ID"
            onClick={onConfirmButtonClick}
          />
        </div>
      )}
    </Modal>
  );
};

export default DeleteApplicationModal;
